(function () {
  var trackedVideos = new WeakSet();
  var trackedVideoDedupeKeys = new Set();
  var context = readProductContext();
  var postHogIdWaiters = [];
  var postHogIdWaitStarted = false;
  var postHogSessionListenerAttached = false;
  var cartAttributeWriteInFlight = false;
  var cartAttributeQueuedOptions = null;
  var cartAttributeSessionKey = '__ph_cart_written';
  var cartWriteTail = Promise.resolve();
  var marketingCartAttributeKey = '__ph_mc_written';
  var marketingCartAttributesWritten = null;
  var marketingPrivacyWait = null;
  var cartSnapshot = null;
  var cartSnapshotVersion = 0;
  var cartReadInFlight = null;
  var checkoutClickInFlight = false;
  var cartPageViewTracked = false;
  var cartDrawerViewTracked = false;
  var productViewTracked = false;
  var openAIContentsViewedSent = false;
  var openAIReelsOpenedSent = false;
  var productViewTimestamp = new Date();
  var clarityPending = [];
  var clarityQueueLoaded = false;
  var clarityFlushTimer = null;
  var clarityQueueKey = '__cts_clarity_pending';
  var clarityTags = {
    VIDEO_PLAYED: ['video_placement'],
    REELS_OPENED: ['video_placement', 'video_id', 'video_index', 'reel_count'],
    REEL_CHANGED: ['video_placement', 'video_id', 'video_index', 'previous_video_id'],
    CART_VIEWED: ['cart_surface'],
    CHECKOUT_BUTTON_CLICKED: ['checkout_source'],
    PRODUCT_ADDED_TO_CART: ['product_id', 'variant_id', 'quantity', 'form_source'],
    ADD_TO_CART_FAILED: ['variant_id', 'quantity', 'form_source', 'error_status', 'error_category'],
  };
  var safeQueryParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'variant',
    'gclid',
    'fbclid',
    'gbraid',
    'wbraid',
    'msclkid',
    'ttclid',
  ];

  function compact(value) {
    if (Array.isArray(value)) {
      return value.map(compact).filter(function (item) {
        return item !== undefined;
      });
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce(function (result, key) {
        var compacted = compact(value[key]);
        if (compacted !== undefined && compacted !== null && compacted !== '') {
          result[key] = compacted;
        }
        return result;
      }, {});
    }

    return value;
  }

  function readProductContext() {
    var el = document.getElementById('CtsProductAnalyticsContext');
    if (!el) return {};

    try {
      return JSON.parse(el.textContent || '{}');
    } catch (error) {
      console.warn('[cts-product-analytics] Invalid product context JSON', error);
      return {};
    }
  }

  function readCookie(name) {
    var escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var match = document.cookie.match(new RegExp('(?:^|; )' + escapedName + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function safePageUrl() {
    try {
      var currentUrl = new URL(window.location.href);
      var sanitizedUrl = new URL(currentUrl.origin + currentUrl.pathname);

      safeQueryParams.forEach(function (param) {
        currentUrl.searchParams.getAll(param).forEach(function (value) {
          sanitizedUrl.searchParams.append(param, value);
        });
      });

      return sanitizedUrl.toString();
    } catch (error) {
      return window.location.origin + window.location.pathname;
    }
  }

  function safeStoredUrl(value, options) {
    options = options || {};
    if (!value) return null;

    try {
      var parsedUrl = new URL(String(value), window.location.origin);
      var sanitizedUrl = new URL(parsedUrl.origin + parsedUrl.pathname);

      safeQueryParams.forEach(function (param) {
        if (param === 'variant') return;

        parsedUrl.searchParams.getAll(param).forEach(function (paramValue) {
          sanitizedUrl.searchParams.append(param, paramValue);
        });
      });

      return options.includeOrigin ? sanitizedUrl.toString() : sanitizedUrl.pathname + sanitizedUrl.search;
    } catch (error) {
      return null;
    }
  }

  function mirrorToClarity(name, props) {
    var keys = clarityTags[name] || (/^MATCHER_[A-Z_]+$/.test(name) ? [] : null);
    if (!keys || !analyticsProcessingAllowed()) return false;
    loadClarityQueue();
    var tags = {};
    keys.forEach(function (key) {
      var value = props && props[key];
      if (value !== undefined && value !== null && value !== '') tags[key] = String(value).slice(0, 255);
    });
    clarityPending.push({ name: name, tags: tags, timestamp: Date.now() });
    persistClarityQueue();
    try {
      if (typeof window.ctsLoadClarity === 'function') window.ctsLoadClarity();
    } catch (error) {
      // A blocked analytics script must not interrupt cart or video controls.
    }
    flushClarityQueue();
    return true;
  }

  // OpenAI Ads (ChatGPT Ads) measurement mirror. snippets/openai-ads-pixel.liquid
  // only defines window.oaiq once Shopify reports marketing consent, but the SDK
  // cannot be unloaded afterwards, so withdrawn consent leaves window.oaiq in
  // place. Check consent here too, or a send during a withdrawn visit would
  // consume a once-per-page flag and silence the real send after a re-grant.
  function sendOpenAIAds(name, data) {
    try {
      if (!openAIAdsAllowed() || typeof window.oaiq !== 'function') return false;
      window.oaiq('measure', name, compact(data));
      return true;
    } catch (error) {
      // Conversion reporting must never break a product page or a cart add.
      return false;
    }
  }

  // Custom events use a different call shape: the literal name 'custom', the
  // custom data type, and the real name in a fourth options argument. OpenAI
  // rejects custom_event_name on a standard event, so these stay separate.
  // Names must be 1-64 characters of letters, digits, underscores or dashes.
  function sendOpenAIAdsCustom(name) {
    try {
      if (!openAIAdsAllowed() || typeof window.oaiq !== 'function') return false;
      window.oaiq('measure', 'custom', { type: 'custom' }, { custom_event_name: name });
      return true;
    } catch (error) {
      // Conversion reporting must never break a product page or a cart add.
      return false;
    }
  }

  // Shopify money values are integers scaled by 100 for every currency,
  // including the ones ISO 4217 gives 0 or 3 decimals. OpenAI Ads wants the
  // real minor unit, so rescale by the currency's own exponent: JPY 123400
  // (Shopify) is 1234 yen, KWD 12345 (Shopify) is 123450 fils.
  var currencyMinorUnitExponents = {
    BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
    BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0,
    PYG: 0, RWF: 0, UGX: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  };

  function presentmentCurrency() {
    try {
      var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (active) return String(active).toUpperCase();
    } catch (error) {
      // Fall through to the Liquid-rendered cart currency.
    }

    return context && context.currency ? String(context.currency).toUpperCase() : null;
  }

  function openAIAdsAmount(shopifyCents, currency) {
    // Number(null) is 0, and a sold-out product page renders a null price.
    if (shopifyCents === null || shopifyCents === undefined || shopifyCents === '') return null;
    var cents = Number(shopifyCents);
    if (!Number.isFinite(cents)) return null;

    var exponent = currencyMinorUnitExponents[currency];
    if (exponent === undefined) exponent = 2;

    return Math.round(cents * Math.pow(10, exponent - 2));
  }

  function sendOpenAIAdsContentsViewed() {
    if (openAIContentsViewedSent || !isProductPage()) return;

    var currency = presentmentCurrency();
    var amount = openAIAdsAmount(context.price_cents, currency);
    var item = {
      id: String(context.variant_id || context.product_id),
      name: context.product_title,
      content_type: 'product',
      quantity: 1,
    };
    var data = { type: 'contents', contents: [item] };

    if (amount !== null && currency) {
      item.amount = amount;
      item.currency = currency;
      data.amount = amount;
      data.currency = currency;
    }

    openAIContentsViewedSent = sendOpenAIAds('contents_viewed', data);
  }

  function sendOpenAIAdsItemsAdded(detail) {
    var currency = presentmentCurrency();
    var quantity = Math.round(Number(detail.quantity));
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

    var unitAmount = openAIAdsAmount(detail.price_cents, currency);
    var item = {
      id: String(detail.variant_id || detail.product_id || ''),
      name: detail.product_title || context.product_title,
      content_type: 'product',
      quantity: quantity,
    };
    var data = { type: 'contents', contents: [item] };

    if (unitAmount !== null && currency) {
      item.amount = unitAmount;
      item.currency = currency;
      data.amount = unitAmount * quantity;
      data.currency = currency;
    }

    sendOpenAIAds('items_added', data);
  }

  function loadClarityQueue() {
    if (clarityQueueLoaded) return;
    clarityQueueLoaded = true;
    try {
      var stored = JSON.parse(window.sessionStorage.getItem(clarityQueueKey) || '[]');
      if (Array.isArray(stored)) clarityPending = stored.filter(function (item) {
        return item && (clarityTags[item.name] || /^MATCHER_[A-Z_]+$/.test(item.name)) &&
          item.tags && typeof item.tags === 'object' && Number.isFinite(item.timestamp);
      });
    } catch (error) {
      // In-memory delivery still works if storage is unavailable.
    }
  }

  function persistClarityQueue() {
    // Only undelivered events survive navigation, for at most two minutes.
    clarityPending = clarityPending.filter(function (item) {
      var age = Date.now() - item.timestamp;
      return age >= 0 && age < 120000;
    }).slice(-20);
    try {
      if (clarityPending.length) window.sessionStorage.setItem(clarityQueueKey, JSON.stringify(clarityPending));
      else window.sessionStorage.removeItem(clarityQueueKey);
    } catch (error) {
      // Storage failures must never interrupt a purchase.
    }
  }

  function flushClarityQueue() {
    clearTimeout(clarityFlushTimer);
    if (!analyticsProcessingAllowed()) clarityPending = [];
    persistClarityQueue();
    if (!clarityPending.length) return;
    // Clarity's runtime exposes .v after replacing the pre-load queue stub.
    // Keep our events out of that page-local stub so navigation cannot lose them.
    if (typeof window.clarity !== 'function' || !window.clarity.v) {
      clarityFlushTimer = setTimeout(flushClarityQueue, 250);
      return;
    }
    while (clarityPending.length) {
      var item = clarityPending.shift();
      persistClarityQueue();
      try {
        (clarityTags[item.name] || []).forEach(function (key) {
          if (typeof item.tags[key] === 'string') window.clarity('set', key, item.tags[key].slice(0, 255));
        });
        window.clarity('event', item.name);
      } catch (error) {
        console.warn('[cts-product-analytics] Clarity capture failed', error);
      }
    }
  }

  function postHogCaptureAllowed() {
    return window.__ctsPostHogCaptureEnabled === true && analyticsProcessingAllowed();
  }

  // Marketing is a separate consent purpose from analytics. Only the OpenAI Ads
  // attribution values below are gated on it.
  function marketingAllowed() {
    try {
      var customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!customerPrivacy || typeof customerPrivacy.marketingAllowed !== 'function') return false;

      return customerPrivacy.marketingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function dataSharingAllowed() {
    try {
      var customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!customerPrivacy || typeof customerPrivacy.saleOfDataAllowed !== 'function') return false;

      return customerPrivacy.saleOfDataAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function openAIAdsAllowed() {
    return marketingAllowed() && dataSharingAllowed();
  }

  // null means the privacy API is unavailable, not an explicit denial.
  function readMarketingPermissions() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.marketingAllowed !== 'function' ||
          typeof privacy.saleOfDataAllowed !== 'function') return null;
      var marketing = privacy.marketingAllowed();
      var dataSharing = privacy.saleOfDataAllowed();
      if (typeof marketing !== 'boolean' || typeof dataSharing !== 'boolean') return null;
      return { marketing: marketing, dataSharing: dataSharing };
    } catch (error) {
      return null;
    }
  }

  function waitForMarketingPrivacy() {
    if (readMarketingPermissions()) return Promise.resolve();
    // Share one bounded loading wait, including its timeout, across queued
    // writes. Later calls still check for a recovered API before using it.
    if (!marketingPrivacyWait) {
      marketingPrivacyWait = new Promise(function (resolve) {
        var deadline = Date.now() + 600;
        function check() {
          if (readMarketingPermissions() || Date.now() >= deadline) {
            resolve();
            return;
          }
          setTimeout(check, 25);
        }
        check();
      });
    }
    return marketingPrivacyWait;
  }

  function capture(name, props, timestamp) {
    if (!name || !analyticsProcessingAllowed()) return;
    mirrorToClarity(name, props);
    if (!postHogCaptureAllowed() || !window.posthog || typeof window.posthog.capture !== 'function') return;
    refreshPixelBridge();

    try {
      window.posthog.capture(name, compact(props || {}), timestamp ? { timestamp: timestamp } : undefined);
    } catch (error) {
      console.warn('[cts-product-analytics] PostHog capture failed', error);
    }
  }

  function isProductPage() {
    return Boolean(context && context.product_id);
  }

  function analyticsProcessingAllowed() {
    try {
      var customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!customerPrivacy || typeof customerPrivacy.analyticsProcessingAllowed !== 'function') return false;

      return customerPrivacy.analyticsProcessingAllowed();
    } catch (error) {
      return false;
    }
  }

  function readPostHogIds() {
    var posthog = window.posthog;
    if (!posthog) return null;

    try {
      var distinctId = typeof posthog.get_distinct_id === 'function' ? posthog.get_distinct_id() : null;
      var sessionId = typeof posthog.get_session_id === 'function' ? posthog.get_session_id() : null;

      distinctId = cleanPostHogIdValue(distinctId);
      sessionId = cleanPostHogIdValue(sessionId);

      if (!distinctId || !sessionId) return null;

      return {
        distinctId: distinctId,
        sessionId: sessionId,
      };
    } catch (error) {
      return null;
    }
  }

  function refreshPixelBridge() {
    if (!postHogCaptureAllowed()) return;
    var ids = readPostHogIds();
    if (!ids) return;

    try {
      var value = encodeURIComponent(JSON.stringify({
        distinctId: ids.distinctId,
        sessionId: ids.sessionId,
        clientId: readCookie('_shopify_y'),
        updatedAt: Date.now(),
      }));
      // Visitor identity lasts one day; the pixel independently expires session linking after 30 minutes.
      document.cookie = '__cts_ph_context=' + value + '; Path=/; Max-Age=86400; SameSite=Lax' +
        (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (error) {
      // The pixel can still use Shopify's client ID and checkout cart attributes.
    }
  }

  function flushPostHogIdWaiters(ids) {
    var waiters = postHogIdWaiters.slice();
    postHogIdWaiters = [];
    postHogIdWaitStarted = false;

    waiters.forEach(function (callback) {
      callback(ids);
    });
  }

  function attachPostHogSessionListener(checkIds) {
    var posthog = window.posthog;
    if (postHogSessionListenerAttached || !posthog || typeof posthog.onSessionId !== 'function') return;

    try {
      posthog.onSessionId(function () {
        checkIds();
      });
      postHogSessionListenerAttached = true;
    } catch (error) {
      postHogSessionListenerAttached = true;
    }
  }

  function withPostHogIds(callback) {
    var existingIds = readPostHogIds();
    if (existingIds) {
      callback(existingIds);
      return;
    }

    postHogIdWaiters.push(callback);
    if (postHogIdWaitStarted) return;

    postHogIdWaitStarted = true;

    var attempts = 0;
    var maxAttempts = 40;
    var delay = 250;

    function checkIds() {
      var ids = readPostHogIds();
      if (ids) {
        flushPostHogIdWaiters(ids);
        return;
      }

      attachPostHogSessionListener(checkIds);

      attempts += 1;
      if (attempts >= maxAttempts) {
        flushPostHogIdWaiters(null);
        return;
      }

      setTimeout(checkIds, delay);
    }

    checkIds();
  }

  function cleanCartAttributeValue(value) {
    if (value === undefined || value === null) return null;
    if (typeof value === 'object') return null;

    var normalized = String(value).trim();
    if (normalized === 'undefined' || normalized === 'null' || normalized === '[object Object]') return null;

    return normalized ? normalized.slice(0, 500) : null;
  }

  function cleanPostHogIdValue(value) {
    var normalized = cleanCartAttributeValue(value);
    if (!normalized) return null;
    if (/@/.test(normalized)) return null;
    if (/\s/.test(normalized)) return null;
    if (/^https?:\/\//i.test(normalized)) return null;

    return normalized;
  }

  function firstTouchValue(firstTouch, key) {
    if (!firstTouch || typeof firstTouch !== 'object') return null;

    var camelKey = key.replace(/_([a-z])/g, function (_match, letter) {
      return letter.toUpperCase();
    });

    if (Object.prototype.hasOwnProperty.call(firstTouch, key)) {
      if (key === 'landing_path') return cleanCartAttributeValue(safeStoredUrl(firstTouch[key], { includeOrigin: false }));
      if (key === 'referrer') return cleanCartAttributeValue(safeStoredUrl(firstTouch[key], { includeOrigin: true }));

      return cleanCartAttributeValue(firstTouch[key]);
    }

    if (Object.prototype.hasOwnProperty.call(firstTouch, camelKey)) {
      if (key === 'landing_path') return cleanCartAttributeValue(safeStoredUrl(firstTouch[camelKey], { includeOrigin: false }));
      if (key === 'referrer') return cleanCartAttributeValue(safeStoredUrl(firstTouch[camelKey], { includeOrigin: true }));

      return cleanCartAttributeValue(firstTouch[camelKey]);
    }

    return null;
  }

  function buildPostHogCartAttributes(ids) {
    var attributes = {
      __ph_did: cleanCartAttributeValue(ids && ids.distinctId),
      __ph_sid: cleanCartAttributeValue(ids && ids.sessionId),
      __ph_session_updated_at: String(Date.now()),
    };
    var firstTouch = window.__ctsFirstTouch;
    var firstTouchKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'gclid',
      'fbclid',
      'gbraid',
      'wbraid',
      'msclkid',
      'ttclid',
      'referrer',
      'landing_path',
      'ts',
    ];

    firstTouchKeys.forEach(function (key) {
      var value = firstTouchValue(firstTouch, key);
      if (value) attributes['__ph_' + key] = value;
    });

    return Object.keys(attributes).reduce(function (result, key) {
      if (attributes[key]) result[key] = attributes[key];
      return result;
    }, {});
  }

  function cartUpdateUrl() {
    return window.routes && window.routes.cart_update_url;
  }

  function cartFetchConfig() {
    if (typeof window.fetchConfig === 'function') return window.fetchConfig();
    if (typeof fetchConfig === 'function') return fetchConfig();

    return null;
  }

  function cartAttributeSessionStorageKey(sessionId) {
    return cartAttributeSessionKey + '_' + cleanCartAttributeValue(sessionId);
  }

  function markInitialCartAttributeWriteComplete(sessionId) {
    try {
      window.sessionStorage.setItem(cartAttributeSessionStorageKey(sessionId), '1');
    } catch (error) {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  function hasInitialCartAttributeWriteCompleted(sessionId) {
    try {
      return window.sessionStorage.getItem(cartAttributeSessionStorageKey(sessionId)) === '1';
    } catch (error) {
      return false;
    }
  }

  // /cart/update.js merges attributes, so two writes in flight at once can send
  // stale companions of whatever each one is updating. Every cart attribute
  // write runs through this one chain instead, and the tail is the last queued
  // write, which is what checkout has to wait for.
  function queueCartWrite(write) {
    function run() {
      try {
        return Promise.resolve(write()).catch(function (error) {
          console.warn('[cts-product-analytics] Cart attribute write failed', error);
          return false;
        });
      } catch (error) {
        console.warn('[cts-product-analytics] Cart attribute write failed', error);
        return false;
      }
    }

    cartWriteTail = cartWriteTail.then(run, run);
    return cartWriteTail;
  }

  function writePostHogCartAttributes(options) {
    options = options || {};

    if (!postHogCaptureAllowed()) return;
    if (!cartUpdateUrl() || typeof window.fetch !== 'function') return;

    if (cartAttributeWriteInFlight) {
      cartAttributeQueuedOptions = Object.assign(cartAttributeQueuedOptions || {}, options);
      return cartAttributeWriteInFlight;
    }

    cartAttributeWriteInFlight = queueCartWrite(function () {
      return postHogCartAttributeWrite(options);
    }).finally(function () {
      var queuedOptions = cartAttributeQueuedOptions;
      cartAttributeWriteInFlight = false;
      cartAttributeQueuedOptions = null;

      if (queuedOptions) {
        writePostHogCartAttributes(queuedOptions);
      }
    });

    return cartAttributeWriteInFlight;
  }

  function postHogCartAttributeWrite(options) {
    return new Promise(function (resolve) {
      withPostHogIds(function (ids) {
        if (!ids) {
          resolve(false);
          return;
        }
        refreshPixelBridge();

        if (options.markSessionWritten && hasInitialCartAttributeWriteCompleted(ids.sessionId)) {
          resolve(true);
          return;
        }

        var attributes = buildPostHogCartAttributes(ids);
        if (!attributes.__ph_did || !attributes.__ph_sid) {
          resolve(false);
          return;
        }

        var config = cartFetchConfig();
        if (!config) {
          resolve(false);
          return;
        }

        config.body = JSON.stringify({ attributes: attributes });

        try {
          window
            .fetch(cartUpdateUrl(), config)
            .then(function (response) {
              if (response && !response.ok) throw new Error('Cart update failed with status ' + response.status);
              if (options.markSessionWritten) markInitialCartAttributeWriteComplete(ids.sessionId);
              resolve(true);
            })
            .catch(function (error) {
              console.warn('[cts-product-analytics] PostHog cart attribute write failed', error);
              resolve(false);
            });
        } catch (error) {
          console.warn('[cts-product-analytics] PostHog cart attribute write failed', error);
          resolve(false);
        }
      });
    });
  }

  function writeInitialProductPageCartAttributes() {
    if (!isProductPage()) return;

    writePostHogCartAttributes({ markSessionWritten: true });
  }

  // The PostHog loader owns first touch, but it publishes window.__ctsFirstTouch
  // from an idle callback that can land after this write, and with the theme
  // setting off it never runs at all. So fall back to the copy it persisted on
  // an earlier page, then to the live URL. Without that, a landing page would
  // forward no click and a later page would clear what the landing page wrote.
  // Marketing and data-sharing permission gate this value, which is passed
  // through untouched.
  function marketingOppref() {
    var oppref = firstTouchValue(window.__ctsFirstTouch, 'oppref');
    if (oppref) return oppref;

    try {
      oppref = firstTouchValue(JSON.parse(window.localStorage.getItem('__ph_first_touch') || 'null'), 'oppref');
    } catch (error) {
      oppref = null;
    }
    if (oppref) return oppref;

    try {
      return cleanCartAttributeValue(new URL(window.location.href).searchParams.get('oppref'));
    } catch (error) {
      return null;
    }
  }

  // The last values written, kept for the whole session so unchanged values are
  // not re-POSTed on every page view. Session storage can be unavailable in
  // strict privacy modes, and then the in-memory copy still dedupes the page.
  function readMarketingCartSignature() {
    try {
      var stored = window.sessionStorage.getItem(marketingCartAttributeKey);
      if (stored !== null) return stored;
    } catch (error) {
      // Fall through to the in-memory copy.
    }

    return marketingCartAttributesWritten;
  }

  function rememberMarketingCartSignature(signature) {
    marketingCartAttributesWritten = signature;

    try {
      if (signature === null) window.sessionStorage.removeItem(marketingCartAttributeKey);
      else window.sessionStorage.setItem(marketingCartAttributeKey, signature);
    } catch (error) {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  // OpenAI permissions and attribution get their own write:
  // it runs on every page type, with or without the PostHog theme setting,
  // analytics consent or PostHog ids, all of which gate the write above.
  // Withdrawn permissions send empty strings rather than omitting the keys,
  // because /cart/update.js merges attributes and an empty value is the only
  // way to delete one a consenting visit already put on the cart. These values
  // are read when the queued write runs, not when it is queued, so a write
  // waiting behind another one still sends what consent says at that moment.
  function writeMarketingCartAttributes() {
    try {
      if (!cartUpdateUrl() || typeof window.fetch !== 'function') return;

      return queueCartWrite(async function () {
        await waitForMarketingPrivacy();
        // Re-read after waiting; a consent change may have happened meanwhile.
        // If loading fails/times out, clear old approval instead of trusting it.
        var permissions = readMarketingPermissions();
        var marketing = permissions && permissions.marketing;
        var dataSharing = permissions && permissions.dataSharing;
        var attributes = {
          // The server requires both flags; missing permission means do not send.
          __ph_mc: marketing ? '1' : '',
          __ph_sod: dataSharing ? '1' : '',
          // OpenAI Ads click attribution, forwarded to checkout for the
          // server-side Conversions API.
          __ph_oppref: (marketing && dataSharing && marketingOppref()) || '',
        };
        var signature = 'v2|' + attributes.__ph_mc + '|' + attributes.__ph_sod + '|' + attributes.__ph_oppref;
        if (permissions && readMarketingCartSignature() === signature) return false;

        var config = cartFetchConfig();
        if (!config) return false;

        config.body = JSON.stringify({ attributes: attributes });
        // product-form.js navigates to the cart 500ms after an add, and a
        // checkout click leaves even sooner. keepalive lets this small POST
        // finish after the page is gone, and the signature is only remembered
        // once Shopify confirms it, so a write that never landed is retried
        // on the next page instead of being treated as done.
        config.keepalive = true;

        return window
          .fetch(cartUpdateUrl(), config)
          .then(function (response) {
            if (response && !response.ok) throw new Error('Cart update failed with status ' + response.status);
            rememberMarketingCartSignature(signature);
            return true;
          })
          .catch(function (error) {
            console.warn('[cts-product-analytics] Marketing cart attribute write failed', error);
            return false;
          });
      });
    } catch (error) {
      console.warn('[cts-product-analytics] Marketing cart attribute write failed', error);
    }
  }

  function baseProps() {
    return Object.assign(
      {
        page_url: safePageUrl(),
        shopify_client_id: readCookie('_shopify_y'),
      },
      context
    );
  }

  function normalizeVideoContent(value) {
    var normalized = String(value || '').toLowerCase();
    if (
      normalized === 'demo' ||
      normalized === 'specs' ||
      normalized === 'install' ||
      normalized === 'review' ||
      normalized === 'unboxing'
    ) {
      return normalized;
    }

    return 'other';
  }

  function normalizeVideoScope(value) {
    var normalized = String(value || '').toLowerCase();
    return normalized === 'car_specific' ? 'car_specific' : 'general';
  }

  function normalizeVideoPlacement(value, element) {
    var normalized = String(value || '').toLowerCase();
    if (normalized === 'product_page' || normalized === 'product_media' || normalized === 'product_reels' || normalized === 'nonproduct_page') return normalized;

    if (element && typeof element.closest === 'function' && element.closest('media-gallery')) return 'product_media';
    if (element && element.classList && element.classList.contains('cts-product-video-facade')) return 'product_page';

    return isProductPage() ? 'product_page' : 'nonproduct_page';
  }

  function normalizeVideoCreator(value) {
    var normalized = String(value || '').toLowerCase();
    if (normalized === 'brand') return 'cts';
    if (normalized === 'customer' || normalized === 'influencer') return normalized;
    return 'cts';
  }

  function normalizeVideoProvider(value) {
    var normalized = String(value || '').toLowerCase();
    if (normalized === 'youtube') return 'youtube';
    if (normalized === 'vimeo') return 'vimeo';
    return 'shopify';
  }

  function isCanonicalVideoId(value) {
    var id = String(value || '');
    return id.indexOf('yt:') === 0 || id.indexOf('sh:') === 0 || id.indexOf('vi:') === 0;
  }

  function providerFromVideoId(value) {
    var id = String(value || '');
    if (id.indexOf('yt:') === 0) return 'youtube';
    if (id.indexOf('sh:') === 0) return 'shopify';
    if (id.indexOf('vi:') === 0) return 'vimeo';
    return null;
  }

  function canonicalVideoId(provider, value) {
    if (value === undefined || value === null || value === '') return null;

    var id = String(value);
    if (isCanonicalVideoId(id)) return id;

    var prefix = 'sh:';
    if (provider === 'youtube') prefix = 'yt:';
    else if (provider === 'vimeo') prefix = 'vi:';
    return prefix + id;
  }

  function playableVideoId(value) {
    var id = String(value || '');
    if (id.indexOf('yt:') === 0 || id.indexOf('sh:') === 0 || id.indexOf('vi:') === 0) return id.slice(3);
    return id;
  }

  function numberOrString(value) {
    if (value === undefined || value === null || value === '') return null;
    var numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric;
  }

  function inferEmbedDetails(html) {
    var source = String(html || '');
    var youtubeMatch =
      source.match(/youtube(?:-nocookie)?\.com\/embed\/([a-zA-Z0-9_-]+)/) ||
      source.match(/[?&]v=([a-zA-Z0-9_-]+)/) ||
      source.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    var vimeoMatch = source.match(/player\.vimeo\.com\/video\/([0-9]+)/);

    if (youtubeMatch) return { provider: 'youtube', id: youtubeMatch[1] };
    if (vimeoMatch) return { provider: 'vimeo', id: vimeoMatch[1] };
    if (/<video[\s>]/i.test(source)) return { provider: 'shopify_hosted', id: null };

    return { provider: 'unknown', id: null };
  }

  function templateHtml(facade) {
    if (!facade || typeof facade.querySelector !== 'function') return '';

    var template = facade.querySelector('template[data-video-embed-template], template');
    return template ? template.innerHTML.trim() : '';
  }

  function inferElementDetails(element) {
    if (!element) return { provider: 'unknown', id: null };

    if (element.tagName === 'VIDEO') {
      return { provider: 'shopify_hosted', id: element.currentSrc || element.src || null };
    }

    if (element.tagName === 'IFRAME') {
      return inferEmbedDetails(element.src || '');
    }

    return inferEmbedDetails(templateHtml(element));
  }

  function isVideoElement(element) {
    if (!element) return false;
    if (element.classList && (element.classList.contains('cts-product-video-facade') || element.classList.contains('pv__facade'))) {
      return true;
    }
    if (element.dataset && (element.dataset.videoId || element.dataset.videoContent || element.dataset.videoProvider)) return true;
    if (element.tagName === 'VIDEO' || element.tagName === 'IFRAME') return true;

    var details = inferElementDetails(element);
    return details.provider !== 'unknown';
  }

  function closestVideoElement(target) {
    if (!target || typeof target.closest !== 'function') return null;

    return target.closest(
      '.cts-product-video-facade, .pv__facade, [data-video-id], [data-video-content], deferred-media, video, iframe'
    );
  }

  function hydrateLegacyFacades() {
    document.querySelectorAll('.cts-product-video-facade').forEach(function (facade) {
      if (facade.dataset.videoId) return;

      var details = inferEmbedDetails(templateHtml(facade));
      if (details.id) {
        facade.dataset.videoId = details.id;
        facade.dataset.videoFallback = 'true';
      }
      if (!facade.dataset.videoProvider || facade.dataset.videoProvider === 'unknown') {
        facade.dataset.videoProvider = details.provider;
        facade.dataset.videoFallback = 'true';
      }

      if (details.provider === 'youtube' && details.id && !facade.querySelector('.cts-product-video-facade__thumb')) {
        var img = document.createElement('img');
        img.className = 'cts-product-video-facade__thumb';
        var hqThumb = 'https://i.ytimg.com/vi/' + details.id + '/hqdefault.jpg';
        var sdThumb = 'https://i.ytimg.com/vi/' + details.id + '/sddefault.jpg';
        // Top out at sddefault, because maxres 404s with a decodable 120x90 gray
        // JPEG for videos lacking an HD/custom thumbnail, which onerror can't catch.
        img.src = hqThumb;
        img.srcset = hqThumb + ' 480w, ' + sdThumb + ' 640w';
        img.sizes = 'auto, 100vw';
        img.onerror = function () {
          img.onerror = null;
          img.srcset = '';
          img.src = hqThumb;
        };
        img.alt = facade.getAttribute('aria-label') || 'Product video';
        img.loading = 'lazy';
        img.width = 1280;
        img.height = 720;
        facade.insertBefore(img, facade.firstChild);
      }
    });
  }

  function verifyFacadeThumbs() {
    document.querySelectorAll('.cts-product-video-facade__thumb').forEach(function (img) {
      if (img.dataset.thumbVerified) return;
      img.dataset.thumbVerified = 'true';

      var verify = function () {
        var chosen = img.currentSrc || img.src;
        if (!chosen || chosen.indexOf('i.ytimg.com') === -1) return;
        if (chosen.indexOf('/hqdefault.jpg') !== -1) return;
        // Missing sd/maxres tiers 404 with a decodable 120x90 gray placeholder,
        // so onload fires and onerror never does. The <img> itself reports a
        // density-corrected naturalWidth for srcset candidates, so measure the
        // raw pixels with a srcset-free probe (served from HTTP cache).
        var probe = new Image();
        probe.onload = function () {
          if (probe.naturalWidth <= 120) {
            img.srcset = '';
            img.removeAttribute('sizes');
            img.src = chosen.replace(/\/[^/]+\.jpg([?#].*)?$/, '/hqdefault.jpg');
          }
        };
        probe.src = chosen;
      };

      if (img.complete && (img.currentSrc || img.src)) {
        verify();
      } else {
        img.addEventListener('load', verify, { once: true });
      }
    });
  }

  function appendAutoplay(url) {
    try {
      var parsed = new URL(url, window.location.href);
      parsed.searchParams.set('autoplay', '1');
      parsed.searchParams.set('playsinline', '1');
      if (!parsed.searchParams.has('rel')) parsed.searchParams.set('rel', '0');
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  function activateLegacyFacade(facade) {
    if (!facade.classList.contains('cts-product-video-facade')) return;
    if (facade.classList.contains('cts-product-video-facade--loaded')) return;

    var html = templateHtml(facade);
    var iframe;

    if (html) {
      var holder = document.createElement('div');
      holder.innerHTML = html;
      iframe = holder.querySelector('iframe, video');

      if (iframe && iframe.tagName === 'IFRAME') {
        iframe.src = appendAutoplay(iframe.src);
        iframe.allowFullscreen = true;
        iframe.allow = iframe.allow || 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      }

      if (iframe && iframe.tagName === 'VIDEO') {
        iframe.autoplay = true;
        iframe.controls = true;
        iframe.playsInline = true;
      }
    } else if (facade.dataset.videoProvider === 'youtube' && facade.dataset.videoId) {
      var youtubeId = playableVideoId(facade.dataset.videoId);
      iframe = document.createElement('iframe');
      iframe.src = 'https://www.youtube.com/embed/' + youtubeId + '?autoplay=1&playsinline=1&rel=0';
      iframe.allowFullscreen = true;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.title = 'YouTube video';
    }

    if (!iframe) return;

    // Let the theme's shared media controls pause page videos when a Reel opens.
    if (iframe.tagName === 'IFRAME') {
      try {
        var embedUrl = new URL(iframe.src, window.location.href);
        if (/(^|\.)youtube(-nocookie)?\.com$/.test(embedUrl.hostname)) {
          embedUrl.searchParams.set('enablejsapi', '1');
          embedUrl.searchParams.set('origin', window.location.origin);
          iframe.src = embedUrl.toString();
          iframe.classList.add('js-youtube');
        }
      } catch (_) {
        // Preserve playback even if an embed URL cannot be enhanced.
      }
    }
    window.pauseAllMedia?.();
    facade.appendChild(iframe);
    facade.classList.add('cts-product-video-facade--loaded', 'pv__facade--loaded');
  }

  function videoElementFromClick(event) {
    var directVideo = closestVideoElement(event.target);
    if (isVideoElement(directVideo)) return directVideo;

    var deferredPoster = event.target.closest('deferred-media .deferred-media__poster');
    var deferredMedia = deferredPoster && deferredPoster.closest('deferred-media');
    if (isVideoElement(deferredMedia)) return deferredMedia;

    var galleryThumbnail = event.target.closest('media-gallery [data-target]');
    var targetId = galleryThumbnail && galleryThumbnail.dataset.target;
    var targetMedia = targetId && (document.getElementById(targetId) || document.getElementById('Slide-' + targetId));
    var targetVideo = targetMedia && targetMedia.querySelector('deferred-media[data-video-id], deferred-media[data-video-content], deferred-media');
    if (isVideoElement(targetVideo)) return targetVideo;

    var galleryNavigation = event.target.closest('media-gallery [data-gallery-nav]');
    if (galleryNavigation) {
      var gallery = galleryNavigation.closest('media-gallery');
      var galleryItems = Array.prototype.slice
        .call(gallery.querySelectorAll('.product__media-item[data-media-id]'))
        .filter(function (item, index) {
          return index === 0 || !item.classList.contains('product__media-item--variant');
        });
      var activeIndex = galleryItems.findIndex(function (item) {
        return item.classList.contains('is-active');
      });
      var offset = galleryNavigation.dataset.galleryNav === 'previous' ? -1 : 1;
      var destinationMedia = galleryItems[activeIndex + offset];
      var destinationVideo = destinationMedia && destinationMedia.querySelector(
        'deferred-media[data-video-id], deferred-media[data-video-content], deferred-media'
      );
      if (isVideoElement(destinationVideo)) return destinationVideo;
    }

    var modalControl = event.target.closest('product-modal [data-media-target], product-modal [data-modal-nav]');
    if (modalControl) {
      var modal = modalControl.closest('product-modal');
      var modalItems = Array.prototype.slice
        .call(modal.querySelectorAll('.product-media-modal__content > [data-media-id]'))
        .filter(function (item, index) {
          return index === 0 || !item.classList.contains('product__media-item--variant');
        });
      var destinationModalMedia;

      if (modalControl.matches('[data-media-target]')) {
        destinationModalMedia = modalItems.find(function (item) {
          return item.dataset.mediaId === modalControl.dataset.mediaTarget;
        });
      } else {
        var activeModalIndex = modalItems.findIndex(function (item) {
          return item.classList.contains('active');
        });
        var modalOffset = modalControl.dataset.modalNav === 'previous' ? -1 : 1;
        destinationModalMedia = modalItems[activeModalIndex + modalOffset];
      }

      var modalVideo =
        destinationModalMedia &&
        (destinationModalMedia.matches('deferred-media')
          ? destinationModalMedia
          : destinationModalMedia.querySelector('deferred-media'));
      if (isVideoElement(modalVideo)) return modalVideo;
    }

    return null;
  }

  function videoProps(facade) {
    var data = facade.dataset || {};
    var details = inferElementDetails(facade);
    var providerMissing = !data.videoProvider || data.videoProvider === 'unknown';
    var provider =
      providerFromVideoId(data.videoId) ||
      normalizeVideoProvider(providerMissing ? details.provider || (data.vid ? 'youtube' : null) : data.videoProvider);
    var id = canonicalVideoId(provider, data.videoId || data.vid || details.id || (data.mediaId ? 'media-' + data.mediaId : null));
    var hasCanonicalId = Boolean(data.videoId && isCanonicalVideoId(data.videoId));
    var fallback =
      data.videoFallback === 'true' ||
      !data.videoContent ||
      !data.videoScope ||
      !data.videoPlacement ||
      !data.videoCreator ||
      providerMissing ||
      !hasCanonicalId;

    var props = Object.assign(baseProps(), {
      video_content: normalizeVideoContent(data.videoContent),
      video_scope: normalizeVideoScope(data.videoScope),
      video_placement: normalizeVideoPlacement(data.videoPlacement, facade),
      video_creator: normalizeVideoCreator(data.videoCreator),
      video_provider: provider,
      video_id: id,
    });

    if (fallback) props.video_fallback = true;

    return props;
  }

  function onVideoClick(event) {
    var facade = videoElementFromClick(event);
    if (!facade || trackedVideos.has(facade)) return;

    var props = videoProps(facade);
    if (props.video_provider === 'shopify') {
      activateLegacyFacade(facade);
      return;
    }

    var videoDedupeKey = [props.video_id, props.video_placement || ''].join('|');

    trackedVideos.add(facade);
    if (!props.video_id || !trackedVideoDedupeKeys.has(videoDedupeKey)) {
      if (props.video_id) trackedVideoDedupeKeys.add(videoDedupeKey);
      capture('VIDEO_PLAYED', props);
    }
    activateLegacyFacade(facade);
  }

  function onNativeVideoPlay(event) {
    var video = event.target;
    if (!video || video.tagName !== 'VIDEO') return;

    var wrapper = video.closest('[data-video-id], [data-video-content], deferred-media') || video;
    if (!isVideoElement(wrapper)) return;
    if (trackedVideos.has(wrapper)) return;

    var props = videoProps(wrapper);
    var videoDedupeKey = [props.video_id, props.video_placement || ''].join('|');

    trackedVideos.add(wrapper);
    if (!props.video_id || !trackedVideoDedupeKeys.has(videoDedupeKey)) {
      if (props.video_id) trackedVideoDedupeKeys.add(videoDedupeKey);
      capture('VIDEO_PLAYED', props);
    }
  }

  function onFacadeKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    var facade = event.target.closest('.cts-product-video-facade');
    if (!facade) return;

    event.preventDefault();
    facade.click();
  }

  function onReelsInteraction(event) {
    var detail = event.detail || {};
    var eventName = { opened: 'REELS_OPENED', changed: 'REEL_CHANGED' }[detail.action];
    if (!eventName || !detail.videoElement) return;

    capture(eventName, Object.assign(videoProps(detail.videoElement), {
      video_index: detail.video_index,
      reel_count: detail.action === 'opened' ? detail.reel_count : undefined,
      previous_video_id: detail.action === 'changed' ? detail.previous_video_id : undefined,
    }));

    // OpenAI Ads gets the reels open as a custom event, at most once per page
    // load. The flag holds the send result, so an open that happens before the
    // pixel has marketing consent retries on the next one. REEL_CHANGED stays
    // PostHog-only: swiping within an open tray is not a separate signal.
    if (detail.action === 'opened' && !openAIReelsOpenedSent) {
      openAIReelsOpenedSent = sendOpenAIAdsCustom('reels_opened');
    }
  }

  function quantityForVariant(variantId) {
    var forms = Array.prototype.slice.call(document.querySelectorAll('product-form form'));

    for (var i = 0; i < forms.length; i += 1) {
      var idInput = forms[i].querySelector('[name="id"]');
      if (!idInput || String(idInput.value) !== String(variantId)) continue;

      var quantityInput = forms[i].querySelector('[name="quantity"]');
      return quantityInput ? Number(quantityInput.value || 1) : 1;
    }

    return null;
  }

  function subscribeToCartErrors() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartError) return;

    subscribe(PUB_SUB_EVENTS.cartError, function (event) {
      var variantId = event && event.productVariantId;
      capture(
        'ADD_TO_CART_FAILED',
        Object.assign(baseProps(), {
          variant_id: numberOrString(variantId),
          quantity: numberOrString((event && event.quantity) || quantityForVariant(variantId)),
          form_source: event && event.source,
          error_status: event && event.status,
          error_category: cartErrorCategory(event && event.status),
        })
      );
    });
  }

  function cartErrorCategory(status) {
    status = Number(status);
    if (status === 429) return 'rate_limited';
    if (status >= 500) return 'server_error';
    if (status >= 400) return 'cart_rejected';
    return 'network_or_response_error';
  }

  function cartProps(cart) {
    if (!cart || !Array.isArray(cart.items)) return {};
    return compact({
      // Shopify cart tokens can include a secret query component. Never send that part.
      cart_id: cart.token ? String(cart.token).split('?')[0] : undefined,
      cart_total: typeof cart.total_price === 'number' ? cart.total_price / 100 : undefined,
      currency: cart.currency,
      line_item_count: cart.items.length,
      total_quantity: cart.item_count,
    });
  }

  function currentCartProps() {
    if (cartSnapshot) return Promise.resolve(cartProps(cartSnapshot));
    if (cartReadInFlight && cartReadInFlight.version === cartSnapshotVersion) return cartReadInFlight.promise;
    var cartUrl = window.routes && window.routes.cart_url;
    if (!cartUrl || typeof window.fetch !== 'function') return Promise.resolve({});
    var version = cartSnapshotVersion;
    var request = window.fetch(cartUrl.replace(/\/$/, '') + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart read failed');
      return response.json();
    }).then(function (cart) {
      // Ignore a read that raced with another cart mutation.
      if (version !== cartSnapshotVersion) return {};
      cartSnapshot = cart;
      return cartProps(cart);
    }).catch(function () { return {}; });

    var promise = Promise.race([
      request,
      new Promise(function (resolve) { setTimeout(function () { resolve({}); }, 800); }),
    ]).finally(function () {
      if (cartReadInFlight && cartReadInFlight.promise === promise) cartReadInFlight = null;
    });
    cartReadInFlight = { version: version, promise: promise };
    return promise;
  }

  function trackCartView(surface) {
    if (!analyticsProcessingAllowed()) return;
    var timestamp = new Date();
    currentCartProps().then(function (props) {
      capture('CART_VIEWED', Object.assign({ page_url: safePageUrl(), cart_surface: surface }, props), timestamp);
    });
  }

  // Owned by the theme rather than the customer-events pixel so the event carries
  // the SDK's $session_id and device props. Fires once per product page load.
  function trackProductView() {
    if (productViewTracked || !isProductPage() || !postHogCaptureAllowed()) return;
    productViewTracked = true;
    capture('PRODUCT_VIEWED', baseProps(), productViewTimestamp);
    sendOpenAIAdsContentsViewed();
  }

  function isCartPage() {
    var cartUrl = window.routes && window.routes.cart_url;
    return cartUrl && window.location.pathname.replace(/\/$/, '') === cartUrl.replace(/\/$/, '');
  }

  function trackVisibleCart() {
    if (!analyticsProcessingAllowed()) return;
    if (isCartPage() && !cartPageViewTracked) {
      cartPageViewTracked = true;
      trackCartView('page');
    }
    var drawerOpen = Boolean(document.querySelector('cart-drawer.active'));
    if (!drawerOpen) cartDrawerViewTracked = false;
    if (drawerOpen && !cartDrawerViewTracked) {
      cartDrawerViewTracked = true;
      trackCartView('drawer');
    }
  }

  function onCartAdded(event) {
    cartSnapshot = null;
    cartSnapshotVersion += 1;
    refreshPixelBridge();
    // Shopify drops cart attributes while the cart is empty, so anything
    // written before the first item is gone. Forget the session signature and
    // write again, or the cart that reaches checkout carries neither value.
    rememberMarketingCartSignature(null);
    writeMarketingCartAttributes();
    // Shopify's customer-events pixel owns the PostHog success event.
    mirrorToClarity('PRODUCT_ADDED_TO_CART', event.detail || {});
    sendOpenAIAdsItemsAdded(event.detail || {});
  }

  function subscribeToVariantChanges() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      if (!variant) return;

      context.variant_id = variant.id;
      context.variant_title = variant.title;
      context.variant_sku = variant.sku;
      context.price = typeof variant.price === 'number' ? variant.price / 100 : context.price;
      context.price_cents = variant.price;
    });
  }

  function subscribeToCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      cartSnapshotVersion += 1;
      cartSnapshot = event && event.cartData && Array.isArray(event.cartData.items) ? event.cartData : null;
      writePostHogCartAttributes();
    });
  }

  function subscribeToVisitorConsentCollected() {
    if (!document || typeof document.addEventListener !== 'function') return;

    function syncAnalyticsConsent() {
      if (!analyticsProcessingAllowed()) {
        document.cookie = '__cts_ph_context=; Path=/; Max-Age=0; SameSite=Lax';
        clarityPending = [];
        flushClarityQueue();
        return;
      }
      loadClarityQueue();
      flushClarityQueue();
      if (postHogCaptureAllowed()) withPostHogIds(refreshPixelBridge);
      writeInitialProductPageCartAttributes();
      trackProductView();
      trackVisibleCart();
      document.dispatchEvent(new Event('cts:analytics-consent-enabled'));
    }
    // The marketing attributes are written whatever analytics consent says, so
    // they sit outside syncAnalyticsConsent and its early return.
    function syncConsent() {
      writeMarketingCartAttributes();
      syncAnalyticsConsent();
    }
    document.addEventListener('visitorConsentCollected', syncConsent);
    document.addEventListener('cts:posthog-capture-enabled', function () {
      withPostHogIds(refreshPixelBridge);
      writeInitialProductPageCartAttributes();
      trackProductView();
      trackVisibleCart();
    });
    // Clarity consent must work even when the PostHog theme setting is disabled.
    if (window.Shopify && window.Shopify.customerPrivacy) syncConsent();
    else if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) syncConsent();
      });
    }
  }

  function isCheckoutTrigger(element) {
    if (!element || element.dataset.ctsPostHogBridgeReady === 'true') return false;

    var label = (element.textContent || element.value || '').trim().toLowerCase();
    var href = element.getAttribute && element.getAttribute('href');
    var name = element.getAttribute && element.getAttribute('name');

    return name === 'checkout' || label === 'check out' || label === 'checkout' || href === '/checkout';
  }

  // Shopify's dynamic checkout buttons: "Buy it now" / wallet buttons on the product
  // page and the wallet buttons under the cart page's Check out button. These bypass
  // the cart and the checkout information page, so checkout_started never fires.
  function acceleratedCheckoutSource(target) {
    if (!target || typeof target.closest !== 'function') return null;
    var container = target.closest('.shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart');
    if (!container) return null;
    if (target.closest('.shopify-payment-button__more-options')) return 'product_more_options';
    return container.matches('shopify-accelerated-checkout-cart') ? 'cart_accelerated' : 'product_buy_now';
  }

  function releaseCheckoutTrigger(element) {
    // The upsell app can replay this click asynchronously before navigating.
    // Keep the guard until another real user click so one attempt is counted once.
    element.dataset.ctsPostHogBridgeReady = 'true';
    element.click();
  }

  function waitForCartAttributeWriteBeforeCheckout(event) {
    var acceleratedSource = acceleratedCheckoutSource(event.target);
    if (acceleratedSource) {
      // Wallet sheets need the original user gesture, so never block or replay this click.
      capture('CHECKOUT_BUTTON_CLICKED', Object.assign(baseProps(), { checkout_source: acceleratedSource }));
      return;
    }
    var target = event.target && event.target.closest && event.target.closest('button, input, a');
    if (target && event.isTrusted) delete target.dataset.ctsPostHogBridgeReady;
    if (!isCheckoutTrigger(target)) return;

    if (checkoutClickInFlight) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    var timestamp = new Date();
    var props = {
      page_url: safePageUrl(),
      checkout_source: target.closest('cart-drawer') ? 'cart_drawer' : isCartPage() ? 'cart_page' :
        target.closest('cart-notification') ? 'cart_notification' : 'other',
    };
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutClickInFlight = true;
    var resolvedCartProps = {};

    // Permission writes must finish independently of analytics/PostHog consent.
    // Refresh them here too, in case the visitor-consent event has not arrived.
    writeMarketingCartAttributes();

    Promise.race([
      Promise.all([
        // Wait on the tail of the queue, not on this write: a consent-clearing
        // marketing write queued behind it has to land before checkout leaves.
        Promise.resolve(writePostHogCartAttributes()).then(function () { return cartWriteTail; }),
        postHogCaptureAllowed() ? currentCartProps().then(function (cart) { resolvedCartProps = cart; }) : Promise.resolve(),
      ]),
      new Promise(function (resolve) {
        setTimeout(resolve, 1200);
      }),
    ]).finally(function () {
      try {
        capture('CHECKOUT_BUTTON_CLICKED', Object.assign(props, resolvedCartProps), timestamp);
      } finally {
        checkoutClickInFlight = false;
        releaseCheckoutTrigger(target);
      }
    });
  }

  window.ctsTrackClarityEvent = mirrorToClarity;
  hydrateLegacyFacades();
  verifyFacadeThumbs();
  document.addEventListener('click', onVideoClick, true);
  document.addEventListener('click', waitForCartAttributeWriteBeforeCheckout, true);
  document.addEventListener('play', onNativeVideoPlay, true);
  document.addEventListener('cts:reels-interaction', onReelsInteraction);
  document.addEventListener('cts:cart-added', onCartAdded);
  // PRODUCT_VIEWED needs PostHog capture consent; the OpenAI pixel needs
  // marketing and data-sharing permission and may initialize before or after us.
  // Both entry points are deduped by openAIContentsViewedSent.
  document.addEventListener('cts:openai-ads-ready', sendOpenAIAdsContentsViewed);
  document.addEventListener('cts:cart-viewed', function () {
    cartDrawerViewTracked = false;
    trackVisibleCart();
  });
  document.addEventListener('submit', refreshPixelBridge, true);
  document.addEventListener('keydown', onFacadeKeydown);
  subscribeToCartErrors();
  subscribeToVariantChanges();
  subscribeToCartUpdates();
  subscribeToVisitorConsentCollected();
  sendOpenAIAdsContentsViewed();
  writeInitialProductPageCartAttributes();
  if (postHogCaptureAllowed()) withPostHogIds(refreshPixelBridge);
  trackVisibleCart();
})();
